import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Wallet, CheckCircle, XCircle, Eye, Clock, UserCheck, UserX, DollarSign } from 'lucide-react';
import walletTopupService from '../services/walletTopupService';
import { showSuccess, showError } from '../services/toastService';
import { getImageUrl } from '../utils/imageUrl';
import './WalletTopupRequests.css';

// UI Components
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const WalletTopupRequests = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(() => searchParams.get('status') || 'pending');
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [requestToApprove, setRequestToApprove] = useState(null);
    const [rejectReason, setRejectReason] = useState('');
    const [processingId, setProcessingId] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0
    });

    useEffect(() => {
        setSearchParams((current) => {
            const next = new URLSearchParams(current);
            if (filter !== 'pending') next.set('status', filter); else next.delete('status');
            return next;
        }, { replace: true });
        fetchRequests();
        fetchStats();
    }, [filter, setSearchParams]);

    const fetchStats = async () => {
        try {
            const response = await walletTopupService.getAllRequests();
            if (response.success) {
                const allRequests = response.data.payments;
                setStats({
                    total: allRequests.length,
                    pending: allRequests.filter(r => r.status === 'pending').length,
                    approved: allRequests.filter(r => r.status === 'approved').length,
                    rejected: allRequests.filter(r => r.status === 'rejected').length
                });
            }
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        }
    };

    const fetchRequests = async () => {
        setLoading(true);
        try {
            let response;
            if (filter === 'pending') {
                response = await walletTopupService.getPendingRequests();
            } else if (filter === 'all') {
                response = await walletTopupService.getAllRequests();
            } else {
                response = await walletTopupService.getAllRequests({ status: filter });
            }

            if (response.success) {
                setRequests(response.data.payments || []);
            }
        } catch (error) {
            console.error('Error fetching requests:', error);
            showError(error.message || 'Failed to fetch requests');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (requestId) => {
        const request = requests.find(r => r.id === requestId);
        setRequestToApprove(request);
        setShowApprovalModal(true);
    };

    const handleApprovalConfirm = async () => {
        if (!requestToApprove) return;

        setProcessingId(requestToApprove.id);
        try {
            const response = await walletTopupService.approveRequest(requestToApprove.id);
            if (response.success) {
                showSuccess('Payment approved and wallet credited successfully!');
                fetchRequests();
                fetchStats();
                setShowApprovalModal(false);
                setRequestToApprove(null);
                setSelectedRequest(null);
                setShowImageModal(false);
            } else {
                showError(response.message || 'Failed to approve payment');
            }
        } catch (error) {
            console.error('Error approving payment:', error);
            showError(error.message || 'Failed to approve payment');
        } finally {
            setProcessingId(null);
        }
    };

    const handleRejectClick = (request) => {
        setSelectedRequest(request);
        setRejectReason('');
        setShowRejectModal(true);
    };

    const handleRejectConfirm = async () => {
        if (!rejectReason.trim()) {
            showError('Please provide a reason for rejection');
            return;
        }

        setProcessingId(selectedRequest.id);
        try {
            const response = await walletTopupService.rejectRequest(selectedRequest.id, rejectReason);
            if (response.success) {
                showSuccess('Payment rejected successfully!');
                fetchRequests();
                fetchStats();
                setShowRejectModal(false);
                setSelectedRequest(null);
                setRejectReason('');
                setShowImageModal(false);
            } else {
                showError(response.message || 'Failed to reject payment');
            }
        } catch (error) {
            console.error('Error rejecting payment:', error);
            showError(error.message || 'Failed to reject payment');
        } finally {
            setProcessingId(null);
        }
    };

    const handleViewImage = (request) => {
        setSelectedRequest(request);
        setShowImageModal(true);
    };

    const getStatusBadge = (status) => {
        const variants = {
            pending: 'pending',
            approved: 'active',
            rejected: 'destructive'
        };
        const texts = {
            pending: 'PENDING',
            approved: 'APPROVED',
            rejected: 'REJECTED'
        };
        return (
            <Badge variant={variants[status] || 'pending'}>
                {texts[status] || 'PENDING'}
            </Badge>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Invalid Date';
            return date.toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch (error) {
            return 'Invalid Date';
        }
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    return (
        <div className="category-management-container wallet-topup-page">
            {/* Header */}
            <div className="cm-header">
                <div>
                    <h1 className="cm-title">Wallet Topup Requests</h1>
                    <p className="cm-subtitle">Manage buyer wallet topup requests</p>
                </div>
            </div>

            {/* Statistics */}
            <div className="um-stats-grid">
                <Card>
                    <CardContent className="um-stat-card">
                        <div className="um-stat-icon">
                            <Wallet size={20} />
                        </div>
                        <div className="um-stat-info">
                            <div className="um-stat-number">{stats.total}</div>
                            <div className="um-stat-label">Total Requests</div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="um-stat-card">
                        <div className="um-stat-icon um-stat-pending">
                            <Clock size={20} />
                        </div>
                        <div className="um-stat-info">
                            <div className="um-stat-number">{stats.pending}</div>
                            <div className="um-stat-label">Pending</div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="um-stat-card">
                        <div className="um-stat-icon um-stat-approved">
                            <UserCheck size={20} />
                        </div>
                        <div className="um-stat-info">
                            <div className="um-stat-number">{stats.approved}</div>
                            <div className="um-stat-label">Approved</div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="um-stat-card">
                        <div className="um-stat-icon um-stat-rejected">
                            <UserX size={20} />
                        </div>
                        <div className="um-stat-info">
                            <div className="um-stat-number">{stats.rejected}</div>
                            <div className="um-stat-label">Rejected</div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Requests List Section */}
            <Card>
                <CardHeader>
                    <CardTitle>Topup Requests</CardTitle>
                </CardHeader>
                <CardContent>
                    {/* Filter */}
                    <div className="um-filters">
                        <Select value={filter} onValueChange={setFilter}>
                            <SelectTrigger className="pm-select-filter">
                                <SelectValue placeholder="All Status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="pending">Pending ({stats.pending})</SelectItem>
                                <SelectItem value="approved">Approved ({stats.approved})</SelectItem>
                                <SelectItem value="rejected">Rejected ({stats.rejected})</SelectItem>
                                <SelectItem value="all">All ({stats.total})</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>Buyer</TableHead>
                                <TableHead>Amount</TableHead>
                                <TableHead>Reference</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Date</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan="7" className="cm-table-empty">Loading...</TableCell>
                                </TableRow>
                            ) : requests.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan="7" className="cm-table-empty">
                                        No {filter !== 'all' ? filter : ''} requests found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                requests.map(request => (
                                    <TableRow key={request.id}>
                                        <TableCell className="cm-id-cell">{request.id}</TableCell>
                                        <TableCell>
                                            <div className="um-business-name">{request.buyer?.business_name || 'N/A'}</div>
                                            <div className="um-email">{request.buyer?.email || 'N/A'}</div>
                                        </TableCell>
                                        <TableCell>
                                            <div style={{ fontWeight: 600, color: '#059669', fontSize: '15px' }}>
                                                {formatAmount(request.amount)}
                                            </div>
                                        </TableCell>
                                        <TableCell className="um-gst">{request.transaction_reference || 'N/A'}</TableCell>
                                        <TableCell>{getStatusBadge(request.status)}</TableCell>
                                        <TableCell className="um-date">{formatDate(request.createdAt)}</TableCell>
                                        <TableCell>
                                            <div className="cm-actions">
                                                {request.payment_proof && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => handleViewImage(request)}
                                                        className="cm-edit-btn"
                                                        title="View Payment Proof"
                                                    >
                                                        <Eye size={16} />
                                                    </Button>
                                                )}
                                                {request.status === 'pending' && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleApprove(request.id)}
                                                            className="um-approve-btn"
                                                            title="Approve Request"
                                                            disabled={processingId === request.id}
                                                        >
                                                            <CheckCircle size={16} />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRejectClick(request)}
                                                            className="um-reject-btn"
                                                            title="Reject Request"
                                                            disabled={processingId === request.id}
                                                        >
                                                            <XCircle size={16} />
                                                        </Button>
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Image Modal */}
            <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
                <DialogContent className="wallet-proof-dialog" style={{ maxWidth: '1000px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {selectedRequest && (
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <DialogHeader>
                                <DialogTitle>Payment Proof - Request #{selectedRequest.id}</DialogTitle>
                            </DialogHeader>
                            <div style={{ marginTop: '24px', overflowY: 'auto', flex: 1, paddingRight: '8px' }}>
                                {/* Two Column Layout: Image (50%) | Details (50%) */}
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                                    {/* Left Side - Payment Proof Image */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                                            Payment Proof
                                        </label>
                                        <div style={{
                                            border: '2px solid #e5e7eb',
                                            borderRadius: '8px',
                                            overflow: 'hidden',
                                            backgroundColor: '#f9fafb',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minHeight: '400px'
                                        }}>
                                            <img
                                                src={getImageUrl(selectedRequest.payment_proof)}
                                                alt="Payment Proof"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '500px',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        </div>
                                    </div>

                                    {/* Right Side - Request Details */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        <div>
                                            <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151', marginBottom: '12px', display: 'block' }}>
                                                Request Details
                                            </label>

                                            {/* Buyer Info */}
                                            <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                                                <label style={{ fontSize: '12px', color: '#6b7280', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Buyer Information
                                                </label>
                                                <p style={{ fontWeight: 600, fontSize: '16px', color: '#111827', marginBottom: '4px' }}>
                                                    {selectedRequest.buyer?.business_name || 'N/A'}
                                                </p>
                                                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '2px' }}>
                                                    {selectedRequest.buyer?.email || 'N/A'}
                                                </p>
                                                {selectedRequest.buyer?.phone && (
                                                    <p style={{ fontSize: '14px', color: '#6b7280' }}>
                                                        {selectedRequest.buyer.phone}
                                                    </p>
                                                )}
                                            </div>

                                            {/* Amount */}
                                            <div style={{ marginBottom: '16px', padding: '16px', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #d1fae5' }}>
                                                <label style={{ fontSize: '12px', color: '#065f46', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Amount
                                                </label>
                                                <p style={{ fontWeight: 700, color: '#059669', fontSize: '24px', margin: 0 }}>
                                                    {formatAmount(selectedRequest.amount)}
                                                </p>
                                            </div>

                                            {/* Reference & Date */}
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
                                                <div style={{ padding: '12px', backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                                    <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        Transaction Reference
                                                    </label>
                                                    <p style={{ fontWeight: 500, fontSize: '14px', color: '#111827', margin: 0, fontFamily: 'monospace' }}>
                                                        {selectedRequest.transaction_reference || 'Not provided'}
                                                    </p>
                                                </div>
                                                <div style={{ padding: '12px', backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                                    <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                        Request Date
                                                    </label>
                                                    <p style={{ fontWeight: 500, fontSize: '14px', color: '#111827', margin: 0 }}>
                                                        {formatDate(selectedRequest.createdAt)}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Status Badge */}
                                            <div style={{ marginTop: '16px', padding: '12px', backgroundColor: '#ffffff', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                                <label style={{ fontSize: '11px', color: '#6b7280', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Status
                                                </label>
                                                {getStatusBadge(selectedRequest.status)}
                                            </div>
                                        </div>

                                        {/* Admin Notes */}
                                        {selectedRequest.admin_notes && (
                                            <div style={{ padding: '12px', backgroundColor: '#fef3c7', borderRadius: '6px', border: '1px solid #fde68a' }}>
                                                <label style={{ fontSize: '11px', color: '#92400e', fontWeight: 600, display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                    Admin Notes
                                                </label>
                                                <p style={{ fontSize: '14px', color: '#78350f', margin: 0 }}>{selectedRequest.admin_notes}</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                {selectedRequest.status === 'pending' && (
                                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid #e5e7eb', position: 'sticky', bottom: 0, backgroundColor: '#ffffff', marginTop: '16px' }}>
                                        <Button
                                            onClick={() => {
                                                setShowImageModal(false); // Close image modal first
                                                setTimeout(() => {
                                                    handleApprove(selectedRequest.id);
                                                }, 100); // Small delay to ensure modal closes
                                            }}
                                            disabled={processingId === selectedRequest.id}
                                            style={{
                                                backgroundColor: '#10b981',
                                                color: '#ffffff',
                                                padding: '10px 20px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontWeight: 500
                                            }}
                                        >
                                            <CheckCircle size={18} />
                                            {processingId === selectedRequest.id ? 'Processing...' : 'Approve Payment'}
                                        </Button>
                                        <Button
                                            onClick={() => {
                                                setShowImageModal(false); // Close image modal first
                                                setTimeout(() => {
                                                    handleRejectClick(selectedRequest);
                                                }, 100); // Small delay to ensure modal closes
                                            }}
                                            disabled={processingId === selectedRequest.id}
                                            style={{
                                                backgroundColor: '#ef4444',
                                                color: '#ffffff',
                                                padding: '10px 20px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                fontWeight: 500
                                            }}
                                        >
                                            <XCircle size={18} />
                                            Reject Payment
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Approval Confirmation Modal */}
            {showApprovalModal && requestToApprove && (
                <div className="modal-overlay" onClick={() => setShowApprovalModal(false)}>
                    <div className="modal-content approval-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Approve Payment Request</h2>
                            <button className="modal-close" onClick={() => setShowApprovalModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="approval-confirmation-content wallet-confirmation-content">
                                <div className="approval-banner">
                                    <span className="approval-icon"><CheckCircle size={20} /></span>
                                    <div className="approval-details">
                                        <h3>Approve wallet topup?</h3>
                                        <p>The amount will be credited to the buyer immediately.</p>
                                    </div>
                                </div>
                                <div className="buyer-summary wallet-summary">
                                    <div className="buyer-summary-field"><span>Buyer</span><strong>{requestToApprove.buyer?.business_name || 'Not provided'}</strong></div>
                                    <div className="buyer-summary-field"><span>Amount</span><strong>{formatAmount(requestToApprove.amount)}</strong></div>
                                    <div className="buyer-summary-field"><span>Reference</span><strong>{requestToApprove.transaction_reference || 'Not provided'}</strong></div>
                                    <div className="buyer-summary-field"><span>Method</span><strong>{requestToApprove.payment_method || 'Not provided'}</strong></div>
                                </div>
                                <div className="approval-note"><strong>After approval</strong><span>The wallet balance and transaction history will update automatically.</span></div>
                            </div>
                        </div>
                        <div className="modal-footer approval-modal-footer">
                            <button
                                className="btn-secondary"
                                onClick={() => setShowApprovalModal(false)}
                                disabled={processingId === requestToApprove.id}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-approve"
                                onClick={handleApprovalConfirm}
                                disabled={processingId === requestToApprove.id}
                            >
                                <CheckCircle size={16} />
                                {processingId === requestToApprove.id ? 'Processing…' : 'Approve Payment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reject Modal */}
            {showRejectModal && selectedRequest && (
                <div className="modal-overlay" onClick={() => setShowRejectModal(false)}>
                    <div className="modal-content reject-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Reject Payment Request</h2>
                            <button className="modal-close" onClick={() => setShowRejectModal(false)}>×</button>
                        </div>
                        <div className="modal-body rejection-modal-body">
                            <div className="rejection-confirmation-content">
                                <div className="rejection-banner">
                                    <span className="rejection-icon"><XCircle size={20} /></span>
                                    <div><h3>Reject wallet topup?</h3><p>The payment will remain uncredited.</p></div>
                                </div>
                                <div className="buyer-summary rejection-buyer-summary wallet-summary">
                                    <div className="buyer-summary-field"><span>Buyer</span><strong>{selectedRequest.buyer?.business_name || 'Not provided'}</strong></div>
                                    <div className="buyer-summary-field"><span>Amount</span><strong>{formatAmount(selectedRequest.amount)}</strong></div>
                                    <div className="buyer-summary-field"><span>Reference</span><strong>{selectedRequest.transaction_reference || 'Not provided'}</strong></div>
                                    <div className="buyer-summary-field"><span>Method</span><strong>{selectedRequest.payment_method || 'Not provided'}</strong></div>
                                </div>
                                <div className="rejection-reason-field">
                                    <label>Reason for rejection <span>*</span></label>
                                    <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter a clear reason. This will be shared with the buyer." rows="3" required />
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer rejection-modal-footer">
                            <button className="btn-secondary" onClick={() => setShowRejectModal(false)}>Cancel</button>
                            <button
                                className="btn-reject"
                                onClick={handleRejectConfirm}
                                disabled={processingId === selectedRequest.id || !rejectReason.trim()}
                            >
                                <XCircle size={16} />
                                {processingId === selectedRequest.id ? 'Processing…' : 'Reject Payment'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WalletTopupRequests;
